import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, FileText, X, Send, CheckCircle, Euro, Calendar,
  Check, Star, Zap, Users, Bot, Clock, Headphones
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusLabels = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-600' },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-600' },
  refused: { label: 'Refusé', color: 'bg-red-100 text-red-600' },
  expired: { label: 'Expiré', color: 'bg-amber-100 text-amber-600' },
};

// Détails complets des forfaits pour un affichage attractif
const offerDetails = {
  'Essentiel': {
    icon: Clock,
    color: 'from-blue-500 to-blue-600',
    borderColor: 'border-blue-500',
    highlights: [
      { icon: Users, text: 'Assistant mi-temps (4h/jour)' },
      { icon: Bot, text: '7 Agents IA inclus' },
      { icon: Headphones, text: 'Support prioritaire' },
    ],
    features: [
      'Gestion administrative',
      'Prise de rendez-vous',
      'Relances clients',
      'CRM Bâtiment inclus',
      'Formation initiale',
      'Suivi hebdomadaire'
    ],
    ideal: 'Idéal pour les artisans et petites entreprises'
  },
  'Business': {
    icon: Zap,
    color: 'from-purple-500 to-purple-600',
    borderColor: 'border-purple-500',
    popular: true,
    highlights: [
      { icon: Users, text: 'Assistant temps plein (40h/sem)' },
      { icon: Bot, text: '7 Agents IA inclus' },
      { icon: Star, text: 'Support VIP' },
    ],
    features: [
      'Tout le forfait Essentiel',
      'Prospection commerciale',
      'Gestion des devis',
      'Suivi chantiers',
      'Reporting mensuel',
      'Accompagnement stratégique'
    ],
    ideal: 'Pour les entreprises en croissance'
  },
  'Premium': {
    icon: Star,
    color: 'from-amber-500 to-amber-600',
    borderColor: 'border-amber-500',
    highlights: [
      { icon: Users, text: '2 Assistants dédiés' },
      { icon: Bot, text: '7 Agents IA + personnalisés' },
      { icon: Star, text: 'Conciergerie business' },
    ],
    features: [
      'Tout le forfait Business',
      'Assistant général + prospection',
      'Personnalisation des agents IA',
      'Intégration outils métier',
      'Reporting avancé',
      'Ligne directe manager'
    ],
    ideal: 'Pour les entreprises ambitieuses'
  }
};

export default function AdminQuotesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  const [newQuote, setNewQuote] = useState({
    prospect_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    offer_ids: [],
    discount_percent: 0,
    notes: '',
    validity_days: 30
  });
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersError, setOffersError] = useState(null);

  useEffect(() => {
    fetchData();
    if (searchParams.get('action') === 'new') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      console.error('[AdminQuotesPage] No admin_token found in localStorage');
      setOffersError('Non authentifié. Reconnecte-toi.');
      setLoading(false);
      setOffersLoading(false);
      return;
    }

    console.log('[AdminQuotesPage] Fetching data with token:', token.substring(0, 10) + '...');

    try {
      const [quotesRes, prospectsRes, offersRes] = await Promise.all([
        fetch(`${API_URL}/api/admin-crm/quotes`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin-crm/prospects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin-crm/offers?t=${Date.now()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      console.log('[AdminQuotesPage] Offers response status:', offersRes.status);

      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (prospectsRes.ok) setProspects(await prospectsRes.json());
      
      if (offersRes.ok) {
        const offersData = await offersRes.json();
        console.log('[AdminQuotesPage] Offers loaded:', offersData.length, 'offers');
        console.log('[AdminQuotesPage] Offers:', offersData.map(o => o.name).join(', '));
        setOffers(offersData);
        setOffersError(null);
      } else {
        console.error('[AdminQuotesPage] Failed to load offers:', offersRes.status);
        setOffersError(`Erreur ${offersRes.status} lors du chargement des forfaits`);
      }
    } catch (error) {
      console.error('[AdminQuotesPage] Error fetching data:', error);
      setOffersError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
      setOffersLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (newQuote.offer_ids.length === 0) {
      toast.error('Sélectionnez au moins un forfait');
      return;
    }
    if (!newQuote.contact_name || !newQuote.contact_email) {
      toast.error('Nom et email requis');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const params = new URLSearchParams();
      if (newQuote.prospect_id) params.append('prospect_id', newQuote.prospect_id);
      params.append('contact_name', newQuote.contact_name);
      params.append('contact_email', newQuote.contact_email);
      if (newQuote.contact_phone) params.append('contact_phone', newQuote.contact_phone);
      if (newQuote.company_name) params.append('company_name', newQuote.company_name);
      newQuote.offer_ids.forEach(id => params.append('offer_ids', id));
      params.append('discount_percent', newQuote.discount_percent.toString());
      if (newQuote.notes) params.append('notes', newQuote.notes);
      params.append('validity_days', newQuote.validity_days.toString());

      const response = await fetch(`${API_URL}/api/admin-crm/quotes?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Devis ${data.quote_number} créé`);
        setShowCreateModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (quoteId, status) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/quotes/${quoteId}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchData();
        if (selectedQuote?.id === quoteId) {
          setSelectedQuote(prev => ({ ...prev, status }));
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleProspectSelect = (prospectId) => {
    const prospect = prospects.find(p => p.id === prospectId);
    if (prospect) {
      setNewQuote({
        ...newQuote,
        prospect_id: prospectId,
        contact_name: prospect.name,
        contact_email: prospect.email,
        contact_phone: prospect.phone || '',
        company_name: prospect.company_name || ''
      });
    }
  };

  const resetForm = () => {
    setNewQuote({
      prospect_id: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      company_name: '',
      offer_ids: [],
      discount_percent: 0,
      notes: '',
      validity_days: 30
    });
  };

  const calculateTotal = () => {
    const subtotal = newQuote.offer_ids.reduce((sum, id) => {
      const offer = offers.find(o => o.id === id);
      return sum + (offer?.price || 0);
    }, 0);
    const discount = subtotal * (newQuote.discount_percent / 100);
    const tax = (subtotal - discount) * 0.20;
    return { subtotal, discount, tax, total: subtotal - discount + tax };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.quote_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         q.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || q.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totals = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devis</h1>
          <p className="text-gray-500">Propositions commerciales</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-quote-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau devis
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(statusLabels).map(([value, { label }]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Quotes List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {filteredQuotes.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun devis</p>
            <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              Créer un devis
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredQuotes.map((quote) => {
              const status = statusLabels[quote.status] || statusLabels.draft;
              return (
                <div
                  key={quote.id}
                  onClick={() => {
                    setSelectedQuote(quote);
                    setShowDetailModal(true);
                  }}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  data-testid={`quote-row-${quote.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{quote.quote_number}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{quote.contact_name}</p>
                      {quote.company_name && (
                        <p className="text-xs text-gray-400">{quote.company_name}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900">{formatCurrency(quote.total)}</p>
                      <p className="text-xs text-gray-400">{formatDate(quote.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="create-quote-modal"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Nouveau devis</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateQuote} className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Prospect Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prospect existant</label>
                  <select
                    value={newQuote.prospect_id}
                    onChange={(e) => handleProspectSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    data-testid="quote-prospect-select"
                  >
                    <option value="">Nouveau contact</option>
                    {prospects.filter(p => p.status !== 'won' && p.status !== 'lost').map(p => (
                      <option key={p.id} value={p.id}>{p.name} {p.company_name ? `(${p.company_name})` : ''}</option>
                    ))}
                  </select>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <input
                      type="text"
                      required
                      value={newQuote.contact_name}
                      onChange={(e) => setNewQuote({ ...newQuote, contact_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      data-testid="quote-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={newQuote.contact_email}
                      onChange={(e) => setNewQuote({ ...newQuote, contact_email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      data-testid="quote-contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={newQuote.contact_phone}
                      onChange={(e) => setNewQuote({ ...newQuote, contact_phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                    <input
                      type="text"
                      value={newQuote.company_name}
                      onChange={(e) => setNewQuote({ ...newQuote, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                {/* Offers Selection - Beautiful cards */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">Choisis la formule *</label>
                  
                  {/* Loading state */}
                  {offersLoading && (
                    <div className="flex items-center justify-center py-12 bg-gray-50 rounded-xl">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mr-3"></div>
                      <span className="text-gray-600">Chargement des forfaits...</span>
                    </div>
                  )}
                  
                  {/* Error state */}
                  {offersError && !offersLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                      <p className="text-red-600 font-medium mb-3">{offersError}</p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => { setOffersLoading(true); fetchData(); }}
                        className="border-red-300 text-red-600 hover:bg-red-100"
                      >
                        Réessayer
                      </Button>
                    </div>
                  )}
                  
                  {/* Empty state */}
                  {!offersLoading && !offersError && offers.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                      <p className="text-amber-700">Aucun forfait disponible. Veuillez contacter l'administrateur.</p>
                    </div>
                  )}
                  
                  {/* Offers grid */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {!offersLoading && !offersError && offers.map(offer => {
                      const details = offerDetails[offer.name] || {};
                      const Icon = details.icon || Star;
                      const isSelected = newQuote.offer_ids.includes(offer.id);
                      
                      return (
                        <motion.div
                          key={offer.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isSelected) {
                              setNewQuote({ ...newQuote, offer_ids: newQuote.offer_ids.filter(id => id !== offer.id) });
                            } else {
                              // Only allow one formula selection at a time
                              setNewQuote({ ...newQuote, offer_ids: [offer.id] });
                            }
                          }}
                          className={`relative rounded-2xl cursor-pointer transition-all border-2 ${
                            isSelected 
                              ? `ring-4 ring-green-500 ring-offset-2 border-green-500 shadow-lg shadow-green-200` 
                              : 'border-transparent hover:shadow-lg'
                          }`}
                          data-testid={`offer-card-${offer.id}`}
                        >
                          {details.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                              <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                POPULAIRE
                              </span>
                            </div>
                          )}
                          
                          <div className={`bg-gradient-to-br ${details.color || 'from-gray-500 to-gray-600'} rounded-t-2xl p-4 text-white`}>
                            <div className="flex items-center justify-between">
                              <Icon className="w-6 h-6" />
                              {isSelected && (
                                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-green-600" />
                                </div>
                              )}
                            </div>
                            <h3 className="text-xl font-bold mt-2">{offer.name}</h3>
                            <div className="flex items-baseline gap-1 mt-1">
                              <span className="text-3xl font-bold">{formatCurrency(offer.price).replace('€', '').trim()}</span>
                              <span className="text-white/80">€/mois</span>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-b-2xl p-4 border border-t-0 border-gray-200">
                            {/* Highlights */}
                            <div className="space-y-2 mb-4">
                              {details.highlights?.map((h, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <h.icon className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium">{h.text}</span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Features */}
                            <div className="space-y-1.5 pt-3 border-t border-gray-100">
                              {details.features?.slice(0, 4).map((f, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                                  <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{f}</span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Ideal for */}
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs text-gray-500 italic">{details.ideal}</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Discount & Validity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remise (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={newQuote.discount_percent}
                      onChange={(e) => setNewQuote({ ...newQuote, discount_percent: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Validité (jours)</label>
                    <input
                      type="number"
                      min="1"
                      value={newQuote.validity_days}
                      onChange={(e) => setNewQuote({ ...newQuote, validity_days: parseInt(e.target.value) || 30 })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newQuote.notes}
                    onChange={(e) => setNewQuote({ ...newQuote, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    rows={2}
                    placeholder="Notes internes ou message pour le client..."
                  />
                </div>

                {/* Totals */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Sous-total HT</span>
                    <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex justify-between text-sm mb-2 text-green-600">
                      <span>Remise ({newQuote.discount_percent}%)</span>
                      <span>-{formatCurrency(totals.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">TVA (20%)</span>
                    <span>{formatCurrency(totals.tax)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold border-t border-gray-200 pt-3 mt-3">
                    <span>Total TTC</span>
                    <span className="text-blue-600">{formatCurrency(totals.total)}/mois</span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-blue-600" disabled={newQuote.offer_ids.length === 0} data-testid="submit-quote-btn">
                    Créer le devis
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{selectedQuote.quote_number}</h2>
                    <p className="text-gray-500">{selectedQuote.contact_name}</p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Status */}
                <div>
                  <h4 className="font-semibold mb-2">Statut</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusLabels).map(([value, { label, color }]) => (
                      <button
                        key={value}
                        onClick={() => handleUpdateStatus(selectedQuote.id, value)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                          selectedQuote.status === value ? color : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Créé le</span>
                    <p className="font-medium">{formatDate(selectedQuote.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Valide jusqu'au</span>
                    <p className="font-medium">{formatDate(selectedQuote.valid_until)}</p>
                  </div>
                </div>

                {/* Items */}
                {selectedQuote.items?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Forfaits</h4>
                    <div className="space-y-2">
                      {selectedQuote.items.map((item, i) => (
                        <div key={i} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                          <span>{item.offer_name}</span>
                          <span className="font-semibold">{formatCurrency(item.price)}/mois</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total TTC</span>
                    <span className="text-blue-600">{formatCurrency(selectedQuote.total)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex justify-end">
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
